import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, Send, Smartphone, Sparkles, ArrowRight, Info, AlertTriangle } from "lucide-react";
import { api, apiError, money } from "../api";
import { PageLoader, EmptyState, Spinner, Toast, useToast } from "../components/ui";
import { enablePush, disablePush, pushSupported, isIOS, isStandalone } from "../push";

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", { timeZone: "Indian/Mauritius", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function Alerts() {
  const navigate = useNavigate();
  const { toast, show, close } = useToast();
  const [data, setData] = useState(null);
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadDevices = useCallback(async () => {
    try {
      const { data } = await api.get("/push/devices");
      setDevices(data.devices);
    } catch (e) { console.error("Failed to load push devices:", e); }
  }, []);

  useEffect(() => {
    api.get("/alerts").then((r) => setData(r.data)).catch((e) => { show(apiError(e), "error"); setData({ alerts: [], deliveries: [] }); });
    loadDevices();
  }, [show, loadDevices]);

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      show("Push notifications enabled on this device.");
      loadDevices();
    } catch (e) {
      show(e.message || apiError(e), "error");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await disablePush();
      show("Push disabled on this device.");
      loadDevices();
    } catch (e) {
      show(e.message || apiError(e), "error");
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/push/test");
      show(data.sent > 0 ? "Test notification sent." : "No registered devices to notify.", data.sent > 0 ? "success" : "error");
    } catch (e) {
      show(apiError(e), "error");
    } finally { setBusy(false); }
  };

  if (!data) return <PageLoader label="Loading alerts" />;

  const iosNeedsInstall = isIOS() && !isStandalone();

  return (
    <div className="fade-in pb-6">
      <Toast toast={toast} onClose={close} />
      <h1 className="font-display text-2xl md:text-3xl text-ink mb-1">Alerts</h1>
      <p className="text-sm text-muted mb-6">Push notifications and recent price changes.</p>

      {/* Push controls */}
      <div className="card p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Bell className="w-5 h-5 text-teal" />
          <h2 className="font-display text-lg text-ink">Browser push</h2>
        </div>

        {iosNeedsInstall && (
          <div className="flex items-start gap-2 bg-amber-light text-amber text-sm p-3 rounded-card mb-4" data-testid="ios-guidance">
            <Smartphone className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              On iPhone, push requires Safari: tap <strong>Share</strong> → <strong>Add to Home Screen</strong>, then open the installed PWA and enable push from there.
            </div>
          </div>
        )}

        {!pushSupported() && !iosNeedsInstall && (
          <div className="flex items-start gap-2 bg-stone-border/40 text-muted text-sm p-3 rounded-card mb-4">
            <Info className="w-5 h-5 shrink-0 mt-0.5" /> This browser does not support push notifications.
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button className="btn-primary flex-1" onClick={enable} disabled={busy || iosNeedsInstall} data-testid="push-enable">
            {busy ? <Spinner /> : <Bell className="w-4 h-4" />} Enable
          </button>
          <button className="btn-ghost flex-1" onClick={disable} disabled={busy} data-testid="push-disable">
            <BellOff className="w-4 h-4" /> Disable
          </button>
          <button className="btn-accent flex-1" onClick={test} disabled={busy} data-testid="push-test">
            <Send className="w-4 h-4" /> Test push
          </button>
        </div>

        <div className="mt-4">
          <div className="text-sm font-medium text-ink mb-2">Registered devices ({devices.length})</div>
          {devices.length === 0 ? (
            <p className="text-sm text-muted">No devices registered yet.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="device-list">
              {devices.map((d) => (
                <li key={d.id} className="flex items-center gap-2 text-sm text-muted">
                  <Smartphone className="w-4 h-4" /> {d.device_label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent changes */}
      <h2 className="font-display text-lg text-ink mb-3">Recent price changes</h2>
      {data.alerts.length === 0 ? (
        <EmptyState icon={Bell} title="No alerts yet" description="When a tracked product's maximum retail price changes, it will appear here." testId="alerts-empty" />
      ) : (
        <div className="space-y-3" data-testid="alerts-list">
          {(data.alerts || []).map((a) => (
            <button key={a.id} onClick={() => navigate(`/scans/${a.scan_id}`)} className="card p-4 w-full text-left" data-testid={`alert-${a.id}`}>
              <div className="flex items-center gap-2 mb-1">
                {a.type === "new" ? (
                  <span className="chip bg-teal-light text-teal"><Sparkles className="w-3 h-3" /> New</span>
                ) : a.type === "no_rows" ? (
                  <span className="chip bg-amber-light text-amber"><AlertTriangle className="w-3 h-3" /> No data</span>
                ) : (
                  <span className="chip bg-coral-light text-coral">Changed</span>
                )}
                <span className="text-xs text-muted ml-auto">{fmt(a.created_at)}</span>
              </div>
              <div className="text-ink truncate">{a.description}</div>
              <div className="text-xs text-muted mb-1">{a.profile_name}{a.ean ? ` · ${a.ean}` : ""}</div>
              {a.type === "changed" && (
                <div className="text-sm font-medium">{money(a.old_price)} <ArrowRight className="w-4 h-4 inline text-coral" /> <span className="text-coral">{money(a.new_price)}</span></div>
              )}
              {a.type === "new" && <div className="text-sm font-medium text-emerald">{money(a.new_price)}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
